// hubspot-chat-api.ts
/* ─────────────────────────────── Types ──────────────────────────────── */
type WidgetOptions = Record<string, unknown>;

export type HubSpotEvent =
    | 'conversationStarted'
    | 'conversationClosed'
    | 'unreadConversationCountChanged'
    | 'statusChanged';

interface HubSpotConversations {
    widget: {
        load(opts?: WidgetOptions): void;
        refresh(): void;
        open(): void;
        close(): void;
        remove(): void;
        resetAndReloadWidget(): void;
    };
    on(event: HubSpotEvent, handler: (payload: unknown) => void): void;
    off(event: HubSpotEvent, handler: (payload: unknown) => void): void;
}

declare global {
    interface Window {
        HubSpotConversations?: HubSpotConversations;
        hsConversationsOnReady?: (() => void)[];
        hsConversationsSettings?: Record<string, unknown>;
    }
}

/* ────────────────────────── Loader parameters ───────────────────────── */
interface LoaderOptions {
    /** *Required.* Your Hub Spot portal / account ID, e.g. “1234567” */
    portalId: string;
    /**
     * **Optional.** Custom script URL.
     * Useful for EU-data-centre scripts (`https://js-eu1.hs-scripts.com/...`)
     * or self-hosted copies.
     */
    scriptSrc?: string;
    /**
     * **Optional.** Whether the widget should auto-load immediately.
     * (Default is HubSpot’s own behaviour, which is *true* unless you set
     * `loadImmediately: false` via `settings`.)
     */
    loadImmediately?: boolean;
    /**
     * **Optional.** Locale code such as `"en-gb"` or `"fr"`.
     * If provided, HubSpot will attempt to translate UI strings.
     */
    locale?: string;
}

/* ─────────────────────────── Wrapper class ──────────────────────────── */
export class HubSpotChatAPI {
    private readonly sdkReady: Promise<HubSpotConversations>;

    /**
     * @param loader   Options that govern **how we load the SDK script**.
     * @param settings Optional `hsConversationsSettings` merged in *before* load.
     */
    constructor(
        loader: LoaderOptions,
        settings: Record<string, unknown> = {}
    ) {
        const { portalId, scriptSrc, loadImmediately, locale } = loader;

        /* ---------- 1.  Inject the <script> tag if needed ---------- */
        const scriptAlready = document.querySelector<HTMLScriptElement>(
            `script[data-hubspot-portal="${portalId}"]`
        );
        if (!scriptAlready) {
            const src =
                scriptSrc ??
                `https://js${locale ? `.${locale}` : ''}.hs-scripts.com/${portalId}.js`;

            const s = document.createElement('script');
            s.async = true;
            s.defer = true;
            s.src = src;
            s.dataset.hubspotPortal = portalId; // easy dedup marker
            document.head.appendChild(s);
        }

        /* ---------- 2.  Promise that resolves once SDK is ready ---------- */
        this.sdkReady = new Promise<HubSpotConversations>((resolve) => {
            const init = () => {
                // Merge caller-supplied widget settings *before* any auto-load
                const merged = {
                    ...(window.hsConversationsSettings ?? {}),
                    ...settings,
                };
                if (loadImmediately === false) merged.loadImmediately = false;
                window.hsConversationsSettings = merged;

                if (!window.HubSpotConversations) {
                    throw new Error(
                        'HubSpot Conversations SDK failed to initialise on window'
                    );
                }
                resolve(window.HubSpotConversations);
            };

            if (window.HubSpotConversations) {
                init();
            } else {
                window.hsConversationsOnReady = window.hsConversationsOnReady ?? [];
                window.hsConversationsOnReady.push(init);
            }
        });
    }

    /* ---------- Widget controls ---------- */
    /** Loads the widget and resolves when the iframe is on the page. */
    private _hasLoaded = false;

    public async load(opts?: WidgetOptions): Promise<void> {
        if (this._hasLoaded) return; // already ready

        const sdk = await this.sdkReady;

        return new Promise<void>((resolve) => {
            const finish = () => {
                clearInterval(poll);
                sdk.off?.('statusChanged', finish as any);
                this._hasLoaded = true;
                resolve();
            };

            // 1⃣ native HS event (works when statusChanged fires correctly)
            sdk.on?.('statusChanged', finish as any);

            // 2⃣ DOM fallback – look for the iframe HubSpot injects
            const isIframeThere = () =>
                !!document.querySelector(
                    '#hubspot-messages-iframe-container, iframe[src*="js.hs-scripts.com"]'
                );

            const poll = window.setInterval(() => {
                const st = sdk.widget.status();
                if (st.loaded || isIframeThere()) finish();
            }, 200);

            // 3⃣ last-ditch safety – give up after 8 s and proceed anyway
            setTimeout(finish, 8000);

            // Kick off the real load
            sdk.widget.load(opts);
        });
    }
    refresh() { return this.sdkReady.then(sdk => sdk.widget.refresh()); }
    open() { return this.sdkReady.then(sdk => sdk.widget.open()); }
    close() { return this.sdkReady.then(sdk => sdk.widget.close()); }
    remove() { return this.sdkReady.then(sdk => sdk.widget.remove()); }
    resetAndReload() { return this.sdkReady.then(sdk => sdk.widget.resetAndReloadWidget()); }

    /* ---------- Event helpers ---------- */
    async on<T = unknown>(
        event: HubSpotEvent,
        handler: (payload: T) => void
    ): Promise<() => void> {
        const sdk = await this.sdkReady;
        sdk.on(event, handler as (p: unknown) => void);
        return () => sdk.off(event, handler as (p: unknown) => void);
    }

    async off<T = unknown>(
        event: HubSpotEvent,
        handler: (payload: T) => void
    ): Promise<void> {
        const sdk = await this.sdkReady;
        sdk.off(event, handler as (p: unknown) => void);
    }
}